import { createErrorFrom, FxIdApplicationStoreCreatePaymentHandlerEmbeddingType, FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus, FxIdDomainStoreEnumsSupportedWebPublishingPlatform } from "@fxid/openapi-client";
import OpenApiClient from "@fxid/openapi-client";
import { EFxIdSdkAdapterBuyProductResponseErrorCode, EFxIdSdkAdapterInfoSocialCapability, EFxIdSdkFocusChangedReason, EFxIdSdkGender, FxIdSdkAdapterSocialSettingDefault } from "@fxid/sdk-core";
import { getAuthToken } from "@fxid/shared";
import { EAdsFormats } from "@vkontakte/vk-bridge";
import { default as vkBridge } from "@vkontakte/vk-bridge";
import { deferred, delayPromise } from "../types/FxIdSdkUtils";
import { FxIdSdkBaseAdapter } from "./FxIdSdkBaseAdapter";
export class FxIdSdkAdapterForVkCom extends FxIdSdkBaseAdapter {
    constructor(exportedSdk, game, config, dependencies) {
        super(exportedSdk, game, config, dependencies);
        this.exportedSdk = exportedSdk;
        this.game = game;
        this.config = config;
        this.adapter = "vkcom";
        this.defaultSocialSettings = {
            ...FxIdSdkAdapterSocialSettingDefault,
            ui: {
                ...FxIdSdkAdapterSocialSettingDefault.ui,
                disabled: false
            }
        };
        this._adsRewardedVideoAvailable = false;
        this._adsInterstitialVideoAvailable = false;
    }
    RegisterShareHandlers() {
        const appId = this.config.PlatformData.VkontakteCom?.AppId;
        if (appId === undefined) {
            log.error("No App ID found in config. Skipping registering sharing handlers");
            return Promise.resolve();
        }
        const sharingHandler = this._dependencies.getSharingHandler();
        if (sharingHandler) {
            sharingHandler.enableShareVk?.(() => {
                vkBridge
                    .send("VKWebAppShare", {})
                    .then((data) => {
                    log.info(`Post added with: %o`, data);
                })
                    .catch((error) => {
                    // Ошибка
                    log.info(error);
                });
            });
        }
        return Promise.resolve();
    }
    async BuyProduct(request) {
        const { promise, resolve, reject } = deferred();
        this.buyProductResolve = async (data) => {
            log.info("Buy product resolved %o", data);
            await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint({
                PublicOrderId: createPaymentResult.TransactionId,
                Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Completed
            }).catch((e) => log.error("[VKCOM] Failed to set optimistic status:", e));
            resolve(data);
        };
        this.buyProductReject = async (data) => {
            log.info("Buy product rejected %o", data);
            await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint({
                PublicOrderId: createPaymentResult.TransactionId,
                Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Cancelled
            }).catch((e) => log.error("[VKCOM] Failed to set optimistic status:", e));
            reject(new Error(data));
        };
        const paymentRequest = {
            Token: getAuthToken(),
            Game: this.game,
            Sku: request.sku,
            WebPublishingPlatform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontakteCom,
            EmbeddingType: FxIdApplicationStoreCreatePaymentHandlerEmbeddingType.Embed,
            ProductDescriptionHint: request.productDescriptionHint,
            ProductNameHint: request.productNameHint
        };
        if (request.developerPayload != null) {
            paymentRequest.DeveloperPayload = {
                MerchantDeveloperPayload: request.developerPayload
            };
        }
        const createPaymentResult = await OpenApiClient.Store.fxIdWebFeaturesStoreCreatePaymentEndpoint(paymentRequest);
        log.info("Received result from server: %o", createPaymentResult);
        this.currentOrderId = createPaymentResult.TransactionId;
        try {
            // У VK нет возможности отправить произвольные параметры в запрос платежа, т.ч. идентификатор заказа отправляется в коде товара
            log.info(`Asking vk to buy item ${createPaymentResult.OrderProduct.Sku} with public id ${createPaymentResult.TransactionId}`);
            const result = await vkBridge.send("VKWebAppShowOrderBox", {
                type: "item",
                item: createPaymentResult.TransactionId
            });
            // На Алисе result.status внезапно начал присылать result как bool
            if (result.status === "success" ||
                result.status === true ||
                result.success === true) {
                this.buyProductResolve?.call(this, {
                    transactionId: createPaymentResult.TransactionId,
                    stats: {
                        currency: createPaymentResult.OrderProduct.Currency ?? "",
                        currencyAmount: createPaymentResult.OrderProduct.Price ?? 0,
                        usdAmount: createPaymentResult.OrderProduct.UsdPrice ?? 0
                    }
                });
            }
            else {
                this.buyProductReject?.call(this, `Vkontakte responded with error: ${JSON.stringify(result)}`);
            }
        }
        catch (error) {
            log.error("Error while buying product: %o", error);
            if (Object.prototype.hasOwnProperty.call(error, "error_type")) {
                const vkError = error;
                if (vkError.error_type === "client_error") {
                    this.buyProductReject?.call(this, {
                        error: vkError.error_data.error_reason + " " + vkError.error_data.error_description,
                        errorCode: this.MapVkErrorCode(vkError.error_data.error_code)
                    });
                }
                else if (vkError.error_type === "api_error") {
                    this.buyProductReject?.call(this, { error: vkError.error_data.error_msg });
                }
                else if (vkError.error_type === "auth_error") {
                    this.buyProductReject?.call(this, {
                        error: vkError.error_data.error_reason + " " + vkError.error_data.error_description
                    });
                }
            }
            else {
                this.buyProductReject?.call(this, createErrorFrom(error));
            }
        }
        return promise;
    }
    MapVkErrorCode(vkErrorCode) {
        // https://dev.vk.com/ru/bridge/getting-started#%22error_type%22:%20%22client_error%22
        const VkErrorCodeUserDenied = 4;
        switch (vkErrorCode) {
            case VkErrorCodeUserDenied:
                return EFxIdSdkAdapterBuyProductResponseErrorCode.UserCancelled;
            default:
                return EFxIdSdkAdapterBuyProductResponseErrorCode.Unknown;
        }
    }
    async GetVkUser() {
        this._vkUser ?? (this._vkUser = await vkBridge.send("VKWebAppGetUserInfo"));
        return this._vkUser;
    }
    async GetSocialInfo() {
        // ХАК
        await delayPromise(500);
        const appId = this.config.PlatformData.VkontakteCom?.AppId;
        if (appId == null) {
            throw new Error("No AppId");
        }
        const user = await this.GetVkUser();
        const gender = this.MapVkGender(user.sex);
        let socialFlavor = undefined;
        const url = new URL(window.location.href);
        if (url.searchParams.has("direct") && url.searchParams.get("direct") === "true") {
            socialFlavor = "vkcom-direct";
        }
        if (socialFlavor == null) {
            const ancestorOrigins = location.ancestorOrigins;
            const url = ancestorOrigins == null || ancestorOrigins[0] == null
                ? new URL(location.origin)
                : new URL(location.ancestorOrigins[0]);
            const host = url.host;
            if (host === FxIdSdkAdapterForVkCom.VkComDirectHost) {
                log.info("Detected vkcom direct");
                socialFlavor = "vkcom-direct";
            }
        }
        const accessTokenResult = await vkBridge.send("VKWebAppGetAuthToken", {
            app_id: appId,
            scope: ""
        });
        const vkComData = {
            appId,
            defaultApiVersion: FxIdSdkAdapterForVkCom.VkComApiVersion,
            defaultAccessToken: accessTokenResult.access_token
        };
        const result = {
            social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontakteCom,
            paymentsAvailable: true,
            socialFlavor,
            userId: user.id.toString(),
            firstName: user.first_name,
            lastName: user.last_name,
            gender,
            photo: {
                width: 200,
                height: 200,
                url: user.photo_200
            },
            capabilities: [
                EFxIdSdkAdapterInfoSocialCapability.FriendsList,
                EFxIdSdkAdapterInfoSocialCapability.FriendsInvite,
                EFxIdSdkAdapterInfoSocialCapability.OutgoingLinksForbidden,
                EFxIdSdkAdapterInfoSocialCapability.AdsAvailable
            ],
            vkcom: vkComData
        };
        return result;
    }
    async Initialize() {
        log.info("Initializing VK com adapter");
        const result = await vkBridge.send("VKWebAppInit", {});
        if (!result.result) {
            throw new Error("Failed to initiialize vk bridge");
        }
        const player = await this.GetSocialInfo();
        this._dependencies.setUserId(player.userId);
        this._dependencies.setUserDisplayName(`#${player.userId}`);
        void this.CheckAdsAvailable();
    }
    async CheckAdsAvailable() {
        try {
            const data = await vkBridge.send("VKWebAppCheckNativeAds", { ad_format: EAdsFormats.REWARD });
            if (data.result) {
                log.info("Rewarded ads ready");
                this._adsRewardedVideoAvailable = true;
                this.exportedSdk.DispatchAdsVideoAvailable();
            }
            else {
                log.warn("Rewarded ads not found");
            }
            const dataInterstitial = await vkBridge.send("VKWebAppCheckNativeAds", {
                ad_format: EAdsFormats.INTERSTITIAL
            });
            if (dataInterstitial.result) {
                log.info("Interstitial ads ready");
                this._adsInterstitialVideoAvailable = true;
                this.exportedSdk.DispatchAdsInterstitialAvailable();
            }
            else {
                log.warn("Interstitial ads not found");
            }
        }
        catch (e) {
            log.error(e);
        }
    }
    StoreCurrency() {
        return Promise.resolve(undefined);
    }
    StatInitialize(request) {
        return Promise.resolve(undefined);
    }
    StatEvent(request) {
        return Promise.resolve(undefined);
    }
    async AdsShowVideo() {
        try {
            const data = await vkBridge.send("VKWebAppShowNativeAds", { ad_format: EAdsFormats.REWARD });
            if (data.result) {
                log.info("Ad shown");
                this.exportedSdk.DispatchAdsFinished();
            }
            else {
                log.warn("Ad failed");
                this.exportedSdk.DispatchAdsFailed();
            }
        }
        catch (e) {
            log.error(e);
        }
    }
    IsFocusChanged() {
        const eventHandler = (e) => {
            const { type, data } = e.detail;
            if (type === "VKWebAppViewHide" && "reason" in data) {
                const currentReason = (() => {
                    switch (data.reason) {
                        case "hide":
                            return EFxIdSdkFocusChangedReason.Hint;
                        case "pip":
                            return EFxIdSdkFocusChangedReason.Pip;
                        default:
                            return EFxIdSdkFocusChangedReason.Other;
                    }
                })();
                const REASON_MESSAGES = {
                    [EFxIdSdkFocusChangedReason.Hint]: "пользователь свернул мини-приложение или игру или переключился на другое приложение на мобильном устройстве",
                    [EFxIdSdkFocusChangedReason.Pip]: "пользователь свернул мини-приложение или игру, и она работает в режиме picture-in-picture («картинка в картинке»)",
                    [EFxIdSdkFocusChangedReason.Other]: "другая причина"
                };
                this.exportedSdk.DispatchFocusChanged({
                    focus: false,
                    reason: currentReason,
                    hint: REASON_MESSAGES[currentReason] || REASON_MESSAGES[EFxIdSdkFocusChangedReason.Other]
                });
            }
            if (type === "VKWebAppViewRestore") {
                this.exportedSdk.DispatchFocusChanged({ focus: true });
            }
        };
        vkBridge.subscribe(eventHandler);
    }
    AdsIsVideoReady() {
        void this.CheckAdsAvailable();
        return Promise.resolve(this._adsRewardedVideoAvailable);
    }
    async GetFriends(request) {
        const appId = this.config.PlatformData.VkontakteCom?.AppId;
        if (appId == null) {
            throw new Error("No AppId");
        }
        log.info("Retrieving friends from VkCom");
        const accessTokenResult = await vkBridge.send("VKWebAppGetAuthToken", {
            app_id: appId,
            scope: "friends"
        });
        const getAppFriendsResult = await vkBridge.send("VKWebAppCallAPIMethod", {
            method: "friends.getAppUsers",
            params: {
                v: FxIdSdkAdapterForVkCom.VkComApiVersion,
                access_token: accessTokenResult.access_token
            }
        });
        log.info("getAppFriendsResult: %o", getAppFriendsResult);
        const appFriendsInfo = await vkBridge.send("VKWebAppCallAPIMethod", {
            method: "users.get",
            params: {
                user_ids: getAppFriendsResult.response.join(","),
                fields: "id,first_name,last_name,sex,photo_200",
                v: FxIdSdkAdapterForVkCom.VkComApiVersion,
                access_token: accessTokenResult.access_token
            }
        });
        log.info("appFriendsInfo: %o", appFriendsInfo);
        const result = { friends: [] };
        for (const user of appFriendsInfo.response) {
            const gender = this.MapVkGender(user.sex);
            const friend = {
                uid: user.id.toString(),
                firstName: user.first_name,
                lastName: user.last_name,
                gender,
                photo: {
                    width: 200,
                    height: 200,
                    url: user.photo_200
                }
            };
            result.friends.push(friend);
        }
        return result;
    }
    MapVkGender(sex) {
        let gender = EFxIdSdkGender.Unspecified;
        switch (sex) {
            case 1:
                gender = EFxIdSdkGender.Female;
                break;
            case 2:
                gender = EFxIdSdkGender.Male;
                break;
        }
        return gender;
    }
    async InviteFriends(request) {
        const data = await vkBridge.send("VKWebAppShowInviteBox", { request_id: request.trackString });
        return { friends: [] };
    }
    async JoinCommunity(request) {
        try {
            const data = await vkBridge.send("VKWebAppJoinGroup", { group_id: parseInt(request.community_id) });
            return { success: data.result };
        }
        catch (err) {
            log.error(err);
        }
        return { success: false };
    }
    async AddToFavorites(request) {
        try {
            const data = await vkBridge.send("VKWebAppAddToFavorites");
            return { success: data.result };
        }
        catch (err) {
            log.error(err);
        }
        return { success: false };
    }
    async SendVkBridge(method, props) {
        return await vkBridge.send(method, props);
    }
    AdsIsInterstitialReady() {
        return Promise.resolve(this._adsInterstitialVideoAvailable);
    }
    async AdsShowInterstitial() {
        try {
            const data = await vkBridge.send("VKWebAppShowNativeAds", { ad_format: EAdsFormats.INTERSTITIAL });
            if (data.result) {
                log.info("Ad shown");
                this.exportedSdk.DispatchAdsFinished();
            }
            else {
                log.warn("Ad failed");
                this.exportedSdk.DispatchAdsFailed();
            }
        }
        catch (e) {
            log.error(e);
        }
        return Promise.resolve();
    }
}
FxIdSdkAdapterForVkCom.VkComDirectHost = "m.vk.com";
FxIdSdkAdapterForVkCom.VkComApiVersion = "5.131";
